import { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Coins, CircleNotch } from "@phosphor-icons/react";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";

export default function CreditsWidget() {
  const { t } = useTranslation();
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(null);

  useEffect(() => { api.get("/credits").then((r) => setData(r.data)).catch(() => {}); }, []);

  const buy = async (pack) => {
    setBusy(pack.id);
    try {
      const { data: d } = await api.post(`/credits/packs/${pack.id}/checkout`, { origin_url: window.location.origin });
      window.location.href = d.url;
    } catch (e) { toast.error(apiError(e)); setBusy(null); }
  };

  if (!data) return null;
  const costs = data.costs || {};
  const hasCosts = (costs.product || costs.logo || costs.piece);

  return (
    <div className="border border-gold/25 rounded-xl p-6 bg-gradient-to-b from-gold/[0.06] to-transparent mb-8" data-testid="credits-widget">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <Coins size={28} className="text-gold" weight="fill" />
          <div>
            <div className="text-sm text-neutral-400">{t("credits.title")}</div>
            <div className="text-3xl font-bold text-gold" data-testid="credits-balance">{data.balance}</div>
          </div>
        </div>
        {hasCosts ? (
          <div className="text-xs text-neutral-400 text-right">
            <div>{t("credits.costs", { product: costs.product || 0, logo: costs.logo || 0, piece: costs.piece || 0 })}</div>
            <div className="text-neutral-500">{t("credits.creditsPerConversion")}</div>
          </div>
        ) : <span className="text-xs text-neutral-500">{t("credits.notActivated")}</span>}
      </div>

      {data.packs?.length > 0 && (
        <div className="mt-5">
          <div className="text-sm font-medium mb-2">{t("credits.buyMore")}</div>
          <div className="grid sm:grid-cols-3 gap-3">
            {data.packs.map((p) => (
              <div key={p.id} className="rounded-lg border border-white/10 bg-white/5 p-4 flex flex-col" data-testid={`credit-pack-${p.id}`}>
                <div className="font-medium">{p.name}</div>
                <div className="text-2xl font-bold text-gold mt-1">{p.credits} <span className="text-sm text-neutral-500">{t("credits.creditsWord")}</span></div>
                <Button onClick={() => buy(p)} disabled={busy === p.id} className="mt-3 rounded-full bg-gold text-obsidian hover:bg-gold/90 font-semibold" data-testid={`buy-pack-${p.id}`}>
                  {busy === p.id ? <CircleNotch className="animate-spin" /> : t("credits.buy", { price: p.price })}
                </Button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
